import { v4 as uuid } from 'uuid';

import { requestProjectModelGeoData } from 'src/actions/projects/action-creators/requestProjectModelGeoData';
import { requestProjectModelLabelsAndDescriptions } from 'src/actions/projects/action-creators/requestProjectModelLabelsAndDescriptions';
import {
  projectsActionCreators,
  projectsStardogRequestDispatchers,
} from 'src/actions/projects/actionCreators';
import { Connection } from 'src/store/connections/types';
import { PendingModelData } from 'src/store/state/projects';
import { AppDispatch } from 'src/store/store';
import { ReduxStateGetter } from 'src/store/types';

export const addImportedModelsToProject =
  ({
    projectId,
    databaseId,
    modelNames,
    pendingModelData,
    connection,
  }: {
    projectId: string;
    databaseId: string;
    modelNames: string[];
    pendingModelData?: Record<string, PendingModelData>;
    connection: Connection;
  }) =>
  async (dispatch: AppDispatch, getState: ReduxStateGetter) => {
    const { databases } = getState();
    const { namedGraphsByModel = {}, namespaces = {} } =
      databases.databasesById?.[databaseId] || {};

    const modelNamesAndIds = modelNames.map((modelName) => ({
      modelName,
      modelId: uuid(),
    }));
    dispatch(
      projectsActionCreators.addImportedModelsToProjectStarted({
        id: projectId,
        connectionId: connection.id,
        databaseId,
        databaseNamespaces: namespaces,
        modelNamesAndIds,
      })
    );

    await Promise.all(
      modelNamesAndIds.map(async ({ modelName, modelId }) => {
        const namedGraphs = namedGraphsByModel[modelName];
        const modelPendingData = pendingModelData?.[modelName];

        if (
          namedGraphs &&
          modelPendingData?.classes &&
          modelPendingData?.properties
        ) {
          const { classes, properties } = modelPendingData;

          dispatch(
            projectsActionCreators.addedPendingModelDataToProject({
              id: projectId,
              modelId,
              classes,
              properties,
            })
          );

          const allIris = new Set<string>();
          const classIris = new Set<string>();

          Object.values(classes || {}).forEach(({ iri, parents }) => {
            allIris.add(iri);
            classIris.add(iri);
            parents.forEach((parent) => {
              allIris.add(parent);
              classIris.add(parent);
            });
          });
          Object.values(properties || {}).forEach(
            ({ iri, domain: domains, range: ranges, parents, isDatatype }) => {
              allIris.add(iri);
              domains.forEach((domain) => {
                allIris.add(domain);
                classIris.add(domain);
              });
              ranges.forEach((range) => {
                allIris.add(range);
                if (!isDatatype) {
                  classIris.add(range);
                }
              });
              parents.forEach((parent) => allIris.add(parent));
            }
          );

          await dispatch(
            requestProjectModelLabelsAndDescriptions({
              allIris,
              connection,
              databaseId,
              modelId,
              namedGraphs,
              projectId,
            })
          );

          await dispatch(
            requestProjectModelGeoData({
              allIris: classIris,
              connection,
              databaseId,
              modelId,
              namedGraphs,
              projectId,
            })
          );

          await projectsStardogRequestDispatchers.requestProjectModelRules({
            connection,
            databaseId,
            id: projectId,
            modelId,
            namedGraphs,
          });
        }
      })
    );

    dispatch(
      projectsActionCreators.addImportedModelsToProjectFinished(
        projectId,
        modelNamesAndIds
      )
    );
  };
